import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import Card from "../components/Card";
import TrustedBy from "../components/TrustedBy";
import SecuritySection from "../components/SecuritySection";
import SEO from "../components/SEO";
import StructuredData, {
  createServiceSchema,
  createBreadcrumbSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

// Viewport settings for better animation triggering
const viewportSettings = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -50px 0px",
};

const Funding = () => {
  const traditionalIssues = [
    "Progress updates are delivered infrequently, in various forms and through different channels.",
    "Disorganised documentation that comes with errors & has to be manually reviewed before the disbursement of funds.",
    "Projects get significantly deviated from the approved business plan & aren't completed with the requested funds.",
  ];

  const modernSolutions = [
    "Simplify project's progress monitoring by centralising project data and documentation.",
    "Make shorter payout cycles in smaller amounts based on real performance to ensure efficient capital use.",
    "Have a single secure channel to communicate among developers & other key stakeholders as technical advisors.",
  ];

  const benefits = [
    {
      title: "Single place for developers and inspectors to work together",
      image: "/funding/developers-inspectors.png",
      points: [
        "Easy access to project history and track records that ensures a targeted and effective inspection.",
        "Quick way to provide progress updates and connect to related events.",
        "All inspection and update reports in one place.",
      ],
    },
    {
      title: "Streamlined invoice collection, review and payouts",
      image: "/funding/invoice-tools.png",
      points: [
        "Smooth verification of work complete.",
        "Centralized drawdown requests and disbursement of project funds.",
        "Performance-based payouts in shorter cycles that enable closer control and mitigation of risks.",
      ],
    },
    {
      title: "Real-time budget consumption and liquidity analysis",
      image: "/funding/budget-consumption.png",
      points: [
        "Monitoring of project progress and project delivery performance.",
        "Dynamic drawdown schedule based on real-time progress for an accurate cash flow forecast.",
        "Accurate planning for disbursements of funds according to the most recent project delivery status.",
      ],
    },
    {
      title: "Proactive performance portfolio overview and forecasting",
      image: "/funding/portfolio-performance.png",
      points: [
        "All key project data are consolidated and rolled up to a single place.",
        "Complete overview of project's budget and related timelines.",
        "Centralised liquidity forecast and project delivery performance for the entire portfolio.",
      ],
    },
  ];

  const fundingSeo = seoData.funding;
  const structuredData = [
    createServiceSchema(
      "Funding Solutions",
      fundingSeo.description,
      fundingSeo.url
    ),
    createBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Solutions", url: "/#solutions" },
      { name: "Funding", url: fundingSeo.url },
    ]),
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO
        title={fundingSeo.title}
        description={fundingSeo.description}
        keywords={fundingSeo.keywords}
        url={fundingSeo.url}
      />
      <StructuredData data={structuredData} />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="text-center mb-8">
              <h2 className="text-sm uppercase tracking-wider text-primary-600 font-semibold mb-4">
                Modern software For project Funding monitoring
              </h2>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
                Ensure efficient capital use and{" "}
                <br className="hidden md:block" />
                project delivery performance
              </h1>
              <div className="mt-8">
                <Link to="/contact">
                  <Button variant="primary" size="large">
                    Book free demo
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-12"
            >
              <img
                src="/funding/hero-image.png"
                alt="Funding Dashboard"
                className="w-full"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section */}
      <TrustedBy />

      {/* Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Traditional Way */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportSettings}
              transition={{ duration: 0.6 }}
            >
              <Card className="h-full">
                <img
                  src="/funding/traditional-way.png"
                  alt="Traditional way"
                  className="w-full rounded-lg mb-6"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Recurrent issues when monitoring project funding
                </h3>
                <div className="space-y-4">
                  {traditionalIssues.map((issue, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <img
                        src="/funding/x-icon.svg"
                        alt="Issue"
                        className="w-6 h-6 flex-shrink-0 mt-1"
                      />
                      <p className="text-gray-600">{issue}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* Modern Way */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportSettings}
              transition={{ duration: 0.6 }}
            >
              <Card className="h-full bg-gradient-to-br from-primary-50 to-white border-primary-200">
                <img
                  src="/funding/modern-way.png"
                  alt="Modern way"
                  className="w-full rounded-lg mb-6"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Gain control with centralised and automated release of funds
                </h3>
                <div className="space-y-4">
                  {modernSolutions.map((solution, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <img
                        src="/funding/check-icon.svg"
                        alt="Solution"
                        className="w-6 h-6 flex-shrink-0 mt-1"
                      />
                      <p className="text-gray-600">{solution}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
              Benefits for your business
            </h2>
          </motion.div>

          <div className="space-y-16">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportSettings}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } gap-20 items-center`}
              >
                <div className="flex-1">
                  <img
                    src={benefit.image}
                    alt={benefit.title}
                    className="w-full rounded-lg shadow-lg bg-gray-100"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    {benefit.title}
                  </h3>
                  <ul className="space-y-4">
                    {benefit.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                        <p className="text-gray-600 text-lg">{point}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <SecuritySection />

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Focus on your projects growth.
              <br />
              We'll take care of the reporting.
            </h2>
            <Link to="/contact">
              <Button variant="ghost" size="large">
                Book demo
              </Button>
            </Link>
            <div className="mt-12">
              <p className="text-sm uppercase tracking-wider text-primary-200 mb-2">
                Email
              </p>
              <a
                href="mailto:hello@estabild.com?subject=Interest%20in%20Estabild"
                className="text-2xl font-semibold hover:text-primary-100 transition-colors"
              >
                hello@estabild.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Funding;
